import { formatCreditAmount } from '@/constants/ipeCredits';

export const PLAN_LABELS = {
  free: '免费会员',
  trial: '免费会员',
  silver: '白银会员',
  gold: '黄金会员',
  diamond: '钻石会员',
  standard: '黄金会员',
  pro: '钻石会员',
  personal_basic: '个人初级会员',
  personal_standard: '个人中级会员',
  personal_premium: '个人高级会员',
  team_basic: '团队初级会员',
  team_standard: '团队中级会员',
  team_premium: '团队高级会员',
};

export const RECHARGE_TYPE_LABELS = {
  CREDIT_ONLY: 'IPE豆保级',
  TIER_LADDER: '阶梯升档',
  SHOP_EXPAND: '店铺扩容',
  STORAGE_EXPAND: '空间扩容',
};

export function formatCredits(n) {
  return formatCreditAmount(n);
}

export function formatStorageMb(mb) {
  const n = Number(mb) || 0;
  if (n >= 1024) {
    const gb = n / 1024;
    return Number.isInteger(gb) ? `${gb}GB` : `${gb.toFixed(1)}GB`;
  }
  return `${n}MB`;
}

export function maskPhone(phone) {
  const p = String(phone || '').trim();
  if (p.length === 11) return `${p.slice(0, 3)} **** ${p.slice(7)}`;
  return p || '—';
}

export function shopRemaining(quota) {
  const limit = Number(quota.shopLimit) || 3;
  const used = Number(quota.connectedShops) || 0;
  if (quota.shopRemaining != null && quota.shopRemaining !== '') {
    return Number(quota.shopRemaining);
  }
  return Math.max(0, limit - used);
}

const FREE_TIER_CODES = new Set(['free', 'trial']);

const TIER_LEVEL_ORDER = { basic: 1, standard: 2, premium: 3 };

export function normalizeWorkspaceMode(mode) {
  return String(mode || 'personal').trim().toLowerCase() === 'team' ? 'team' : 'personal';
}

/** 工作区中文标签：personal → 个人，team → 团队（通知/告警文案统一用） */
export function workspaceScopeLabel(workspaceMode) {
  return normalizeWorkspaceMode(workspaceMode) === 'team' ? '团队' : '个人';
}

export function tierMatchesScope(tierCode, scope) {
  const code = String(tierCode || '');
  if (scope === 'team') return code.startsWith('team_');
  return code.startsWith('personal_') || (!code.startsWith('team_') && !code.startsWith('personal_'));
}

export function resolveWorkspaceMode(center) {
  return normalizeWorkspaceMode(center?.workspaceModeEffective || center?.workspaceMode);
}

export function resolveScopedQuota(center, workspaceMode) {
  if (!center) return {};
  const scope = normalizeWorkspaceMode(workspaceMode);
  if (scope === 'team' && center.teamQuota) return center.teamQuota;
  return center.personalQuota || center.quota || {};
}

export function localizedPlanTierLabel(t, user, scopedQuota, workspaceMode) {
  const code = resolveCurrentPlanTierCode(user, scopedQuota, workspaceMode);
  if (typeof t === 'function') {
    const translated = t(`accountCenterUi.plan.${code}`, { defaultValue: '' });
    if (translated) return translated;
  }
  return resolveScopedPlanTier(user, scopedQuota, workspaceMode);
}

export function resolveScopedPlanTier(user, scopedQuota, workspaceMode) {
  if (scopedQuota?.planTier) {
    return PLAN_LABELS[scopedQuota.planTier] || scopedQuota.planTierLabel || scopedQuota.planTier;
  }
  const scope = normalizeWorkspaceMode(workspaceMode);
  if (scope === 'team') {
    const tier = user?.teamPlanTier;
    return tier && tier !== 'free' ? (PLAN_LABELS[tier] || tier) : '免费会员';
  }
  const tier = user?.personalPlanTier || user?.planTier;
  return PLAN_LABELS[tier] || tier || '免费会员';
}

export function isPurchasableMembershipTier(row) {
  if (!row) return false;
  if (FREE_TIER_CODES.has(String(row.tier || '').toLowerCase())) return false;
  if (String(row.label || '').includes('免费')) return false;
  const price = Number(row.priceYuan);
  if (!Number.isNaN(price) && price <= 0) return false;
  return true;
}

function resolveCurrentPlanTierCode(user, scopedQuota, workspaceMode) {
  if (scopedQuota?.planTier) return String(scopedQuota.planTier);
  const scope = normalizeWorkspaceMode(workspaceMode);
  if (scope === 'team') {
    const tier = user?.teamPlanTier;
    return tier && tier !== 'free' ? String(tier) : 'free';
  }
  const tier = user?.personalPlanTier || user?.planTier;
  return tier ? String(tier) : 'free';
}

/** 当前账号是否已开通团队会员（主账号），用于创建团队、邀请成员等管理能力 */
export function isTeamManagementAvailable(center) {
  if (center?.teamManagementAvailable != null) {
    return Boolean(center.teamManagementAvailable);
  }
  const tier = String(center?.user?.teamPlanTier || 'free').toLowerCase();
  if (FREE_TIER_CODES.has(tier) || !tier.startsWith('team_')) {
    return false;
  }
  const expiryRaw = center?.teamQuota?.membershipExpiresAt;
  if (!expiryRaw) return true;
  const expiry = new Date(expiryRaw);
  return !Number.isNaN(expiry.getTime()) && expiry.getTime() > Date.now();
}

/** 是否可使用团队工作区（已加入团队且主账号团队会员有效，含普通成员） */
export function isTeamWorkspaceAvailable(center) {
  if (center?.teamWorkspaceAvailable != null) {
    return Boolean(center.teamWorkspaceAvailable);
  }
  return false;
}

/** 头像菜单等入口：主账号或已加入团队的成员均应看到「团队空间」 */
export function isTeamSpaceEntryAvailable(center) {
  return isTeamWorkspaceAvailable(center) || isTeamManagementAvailable(center);
}

export function isMaxMembershipTier(center, workspaceMode) {
  if (!center) return false;
  const scope = normalizeWorkspaceMode(workspaceMode);
  const scopedQuota = resolveScopedQuota(center, scope);
  const user = center.user || {};
  const currentTier = resolveCurrentPlanTierCode(user, scopedQuota, scope);
  if (FREE_TIER_CODES.has(currentTier.toLowerCase())) return false;

  const membershipTiers = center.membershipTiers?.length ? center.membershipTiers : center.usageTiers || [];
  const purchasable = membershipTiers
    .filter((row) => tierMatchesScope(row.tier, scope))
    .filter(isPurchasableMembershipTier);
  if (!purchasable.length) return false;

  const maxTier = purchasable.reduce((best, row) => {
    const bestLevel = TIER_LEVEL_ORDER[best?.tierLevel] || 0;
    const rowLevel = TIER_LEVEL_ORDER[row?.tierLevel] || 0;
    if (rowLevel > bestLevel) return row;
    if (rowLevel < bestLevel) return best;
    return String(row.tier || '') > String(best.tier || '') ? row : best;
  }, purchasable[0]);

  return currentTier === String(maxTier.tier || '');
}
